import React, { useEffect, useState } from 'react';
import { Box, Grid } from '@mui/material';
import Typography from '@mui/material/Typography';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import {
    Dropzone,
    FileMosaic,
    FullScreen,
    ImagePreview,
    VideoPreview,
} from '@dropzone-ui/react';
import { useAuthContext } from '../../../../contexts/authContext';
import Loading from '../loadings/loading';

type Props = {
    name: string;
    accept?: string | undefined;
    maxFiles?: number | undefined;
    onChange: (files: any[]) => void;
    icon?: false | boolean;
    currentFiles?: File[];
    loading?: boolean;
    errors?: any;
};

const DropZoneField = ({
    name,
    accept,
    maxFiles,
    onChange,
    icon,
    currentFiles,
    loading,
    errors
}: Props) => {
    //onChange Formik
    //The variable dropzoneFieldFormik must be equal to the variable name of the formik
    //onChange={(value) => formik.setFieldValue('dropzoneFieldFormik', value)}

    const { modeTheme } = useAuthContext();
    const [files, setFiles] = useState<{ id: number; file: any }[]>([]);
    const [imageSrc, setImageSrc] = useState<any>(undefined);
    const [videoSrc, setVideoSrc] = useState<any>(undefined);

    const updateFiles = (incommingFiles: any) => {
        setFiles(incommingFiles);
        let filesObjects: any[] = [];
        if (incommingFiles.length > 0) {
            for (let i = 0; i < incommingFiles.length; i++) {
                const { file } = incommingFiles[i];
                filesObjects = filesObjects.concat(file);
            }
        }
        onChange(filesObjects);
    };

    const removeFile = (id: any) => {
        setFiles(files.filter((x) => x.id !== id));
        onChange(files.filter((x) => x.id !== id));
    };

    const handleSee = (imageSource: string | undefined) => {
        setImageSrc(imageSource);
    };

    const handleWatch = (videoSource: File | string | undefined) => {
        setVideoSrc(videoSource);
    };

    useEffect(() => {
        if (currentFiles) {
            setFiles(currentFiles.map((file, index) => ({ id: index, file })));
        } else {
            setFiles([]);
        }
    }, [currentFiles]);

    return (
        <Grid position="relative">
            <Dropzone
                name={name}
                onChange={updateFiles}
                value={files}
                autoClean={true}
                accept={accept}
                maxFiles={maxFiles}
                localization="ES-es"
                footer={false}
                header={files.length > 0}
                style={{borderColor: errors ? 'red' : '000'}}
            >
                {files.length === 0 && (
                    <Box
                        mt={2}
                        component="span"
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                        sx={{
                            color:
                                modeTheme === 'darkMode'
                                    ? 'primary.contrastText'
                                    : 'primary.main',
                        }}
                    >
                        <Typography component="span">
                            <strong>
                                Arrastre y suelte algunos archivos aquí,
                            </strong>
                        </Typography>
                        <Typography component="span">
                            <strong>
                                o haga clic para seleccionar archivos
                            </strong>
                        </Typography>
                        <Box mt={1}>
                            <Typography component="span">
                                <>
                                    Tipos de archivos aceptados: <br />
                                    {!accept ? (
                                        <strong>
                                            .png, .jpg, .jpeg, .txt, .csv, .doc,
                                            .docx, .xls, .xlsx, .pdf, <br />{' '}
                                            .video, .audio, .rar, .zip
                                        </strong>
                                    ) : (
                                        <strong>{accept}</strong>
                                    )}
                                </>
                            </Typography>
                        </Box>
                        {icon && (
                            <Box mt={1}>
                                <CloudUploadOutlinedIcon
                                    sx={{
                                        color:
                                            modeTheme === 'darkMode'
                                                ? 'primary.contrastText'
                                                : 'primary.main',
                                        fontSize: '3.5rem',
                                    }}
                                />
                            </Box>
                        )}
                    </Box>
                )}
                {files.length > 0 &&
                    files.map((file: any) => (
                        <FileMosaic
                            {...file}
                            onDelete={removeFile}
                            key={file.id}
                            info
                            preview
                            resultOnTooltip
                            alwaysActive
                            onSee={handleSee}
                            onWatch={() => handleWatch(file.file)}
                        />
                    ))}
            </Dropzone>
            {loading && (
                <Grid
                    position="absolute"
                    top={0}
                    left={0}
                    width="100%"
                    height="100%"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    bgcolor="#0000001A"
                >
                    <Loading />
                </Grid>
            )}
            <FullScreen
                open={imageSrc !== undefined}
                onClose={() => setImageSrc(undefined)}
            >
                <ImagePreview src={imageSrc} />
            </FullScreen>
            <FullScreen
                open={videoSrc !== undefined}
                onClose={() => setVideoSrc(undefined)}
            >
                <VideoPreview src={videoSrc} autoPlay controls />
            </FullScreen>
            <Typography color='#d32f2f' fontSize='0.75rem' textAlign='center'>{errors}</Typography>
        </Grid>
    );
};

export default DropZoneField;
